import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { User } from "#modules/users/infra/fetchUser";
import { userQuery } from "#modules/users/infra/userQuery";
import { ROUTES } from "#shared/routes";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";

import { useLogout } from "../../auth/infra/useLogout";

export const useUser = (options?: { initialData?: User | null }) => {
  const analytics = useAnalytics();
  const hasTrackedSession = useRef(false);

  const navigate = useNavigate();
  const { logout } = useLogout();

  const {
    data: user,
    error,
    ...rest
  } = useQuery({
    ...userQuery(),
    ...(options?.initialData !== undefined && { initialData: options.initialData }),
  });

  useEffect(() => {
    if (error && error instanceof AxiosError) {
      if (error.response?.status === 401) {
        navigate(ROUTES.login);
      }
      if (error.code === "ERR_NETWORK") {
        void logout();
      }
    }

    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        companyId: user.companyId,
      });

      analytics.identify(user.id, {
        email: user.email,
        companyId: user.companyId,
        createdAt: user.createdAt,
      });
      analytics.group("company", user.companyId || "");

      if (!hasTrackedSession.current) {
        hasTrackedSession.current = true;
        analytics.capture(AnalyticsEvent.UserConnected);
      }
    }
  }, [user, navigate, logout, analytics, error]);

  return {
    user: user
      ? {
          ...user,
        }
      : null,
    isFaberaUser: user?.email.includes("@fabera.fr") ?? false,
    ...rest,
  };
};
